import SportIcon from '@/components/SportIcon';
/**
 * TournamentManagement.jsx
 * Components for: Sport Formats, Group Management, Encounters, Group Standings
 */
import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Trophy, Plus, Trash2, Users, CheckCircle2, Clock, Play,
  Edit2, Save, X, ChevronDown, ChevronUp, Target, Award,
  RefreshCw, AlertTriangle, Check, Zap, Settings, Eye
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const GROUP_COLORS = { A: '#ef4444', B: '#3b82f6', C: '#22c55e', D: '#f97316' };

const DEFAULT_COUNTS = {
  table_tennis: { singles_count:6, doubles_count:0, super_match_on_tie:true,  game_points:11, games_per_match:3, ko_games_per_match:5, min_diff:2 },
  badminton:    { singles_count:0, doubles_count:3, super_match_on_tie:false, game_points:21, games_per_match:1, ko_games_per_match:3, min_diff:2 },
  carrom:       { singles_count:0, doubles_count:5, super_match_on_tie:false, game_points:29, games_per_match:1, ko_games_per_match:1, min_diff:2 },
  pickleball:   { singles_count:0, doubles_count:5, super_match_on_tie:false, game_points:15, games_per_match:1, ko_games_per_match:1, min_diff:2 },
  chess:        { singles_count:3, doubles_count:0, super_match_on_tie:false, game_points:1,  games_per_match:1, ko_games_per_match:1, min_diff:0 },
};

// ──────────────────────────────────────────────────────────────
// Sport Format Tab
// ──────────────────────────────────────────────────────────────
export function SportFormatsTab({ tournamentId, orgSlug, availableSports }) {
  const [formats, setFormats]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState('');
  const [editFmt, setEditFmt]   = useState(null); // sport_code being edited

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchFormats(); }, [tournamentId]);

  const fetchFormats = async () => {
    setLoading(true);
    const token = localStorage.getItem('btccp_access_token');
    const res = await fetch(`/api/orgs/${orgSlug}/tournaments/${tournamentId}/sport-formats`,
      { headers: { 'Authorization': `Bearer ${token}` } });
    const data = await res.json();
    setFormats(Array.isArray(data) ? data : []);
    setLoading(false);
  };

  const handleSave = async (fmt) => {
    setSaving(fmt.sport_code);
    const token = localStorage.getItem('btccp_access_token');
    const res = await fetch(`/api/orgs/${orgSlug}/tournaments/${tournamentId}/sport-formats`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(fmt),
    });
    if (res.ok) {
      toast.success(`${fmt.sport_name} format saved`);
      fetchFormats();
      setEditFmt(null);
    } else {
      const d = await res.json();
      toast.error(d.detail || 'Failed to save');
    }
    setSaving('');
  };

  const handleRemove = async (sport_code) => {
    if (!confirm(`Remove ${sport_code} from this tournament?`)) return;
    const token = localStorage.getItem('btccp_access_token');
    await fetch(`/api/orgs/${orgSlug}/tournaments/${tournamentId}/sport-formats/${sport_code}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    fetchFormats();
    toast.success('Sport removed');
  };

  const formatsMap = Object.fromEntries(formats.map(f => [f.sport_code, f]));

  return (
    <div className="space-y-4" data-testid="sport-formats-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-[#f0f2f7]">Sport Formats</h3>
          <p className="text-sm text-[#8a93a3]">Define which sports are played and how many matches per sport</p>
        </div>
        <p className="text-xs text-[#8a93a3]">{formats.length} sports configured</p>
      </div>

      {/* Configured sports */}
      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i=><Skeleton key={i} className="h-16 bg-[#1d2230]"/>)}</div>
      ) : (
        <div className="space-y-3">
          {formats.map(fmt => (
            <Card key={fmt.sport_code} className={`bg-[#161a26] border-[#252a37] ${editFmt===fmt.sport_code?'ring-1 ring-[rgba(245,197,24,0.35)]':''}`}>
              <CardContent className="p-4">
                {editFmt === fmt.sport_code ? (
                  <FormatEditRow fmt={fmt} onSave={handleSave} onCancel={() => setEditFmt(null)} saving={saving===fmt.sport_code} />
                ) : (
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-[#1d2230] rounded-lg">
                        <Trophy className="h-4 w-4 text-[#f5c518]" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#f0f2f7]">{fmt.sport_name}</p>
                        <p className="text-xs text-[#8a93a3]">
                          {fmt.singles_count > 0 && `${fmt.singles_count} Singles`}
                          {fmt.singles_count > 0 && fmt.doubles_count > 0 && ' + '}
                          {fmt.doubles_count > 0 && `${fmt.doubles_count} Doubles`}
                          {fmt.super_match_on_tie && ' · Super match on tie'}
                          {fmt.platinum_count > 0 && <span className="text-[#f5c518] ml-0.5">· ★{fmt.platinum_count} Platinum</span>}
                        </p>
                        {/* Scoring rules */}
                        {fmt.game_points && (
                          <p className="text-[10px] text-[#8a93a3] mt-0.5 flex items-center gap-2">
                            <span className="text-[#f5c518]">{fmt.game_points}pts/game</span>
                            <span>Group: best of {fmt.games_per_match||1}</span>
                            {fmt.ko_games_per_match && fmt.ko_games_per_match !== fmt.games_per_match &&
                              <span className="text-[#f5c518]">· KO: best of {fmt.ko_games_per_match}</span>}
                            {fmt.min_diff > 0 && <span>· +{fmt.min_diff} min diff</span>}
                          </p>
                        )}
                        {(fmt.max_matches_per_player || fmt.min_matches_per_player) && (
                          <p className="text-[10px] mt-0.5">
                            <span className="text-[#f5c518] font-bold">Player limit: </span>
                            <span className="text-[#8a93a3]">
                              min {fmt.min_matches_per_player||1} · max {fmt.max_matches_per_player||'—'} matches per player per tie
                            </span>
                          </p>
                        )}

                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2 text-sm text-[#8a93a3]">
                        <Badge className="bg-[rgba(34,197,94,0.12)] text-[#22c55e] border border-[rgba(34,197,94,0.3)] text-xs">{fmt.win_pts}W</Badge>
                        <Badge className="bg-[rgba(245,197,24,0.12)] text-[#f5c518] border border-[rgba(245,197,24,0.3)] text-xs">{fmt.draw_pts}D</Badge>
                        <Badge className="bg-[rgba(239,68,68,0.12)] text-[#ef4444] border border-[rgba(239,68,68,0.3)] text-xs">{fmt.loss_pts}L</Badge>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => setEditFmt(fmt.sport_code)}
                        className="text-[#8a93a3] hover:text-[#f0f2f7]"><Edit2 className="h-3.5 w-3.5"/></Button>
                      <Button size="sm" variant="ghost" onClick={() => handleRemove(fmt.sport_code)}
                        className="text-[#8a93a3] hover:text-[#ef4444]"><Trash2 className="h-3.5 w-3.5"/></Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add sport */}
      <div>
        <p className="text-xs font-semibold text-[#8a93a3] uppercase tracking-wider mb-2">Add Sport</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {(availableSports || []).filter(s => !formatsMap[s.code]).map(sp => {
            const def = DEFAULT_COUNTS[sp.code] || { singles_count:1, doubles_count:0, super_match_on_tie:false };
            return (
              <Button key={sp.code} variant="outline"
                onClick={() => handleSave({ sport_code:sp.code, sport_name:sp.name, ...def, win_pts:2, draw_pts:1, loss_pts:0 })}
                disabled={saving===sp.code}
                className="border-[#252a37] text-[#8a93a3] hover:text-[#f0f2f7] hover:bg-[#161a26] text-xs justify-start gap-2">
                <Plus className="h-3.5 w-3.5 flex-shrink-0"/>
                {sp.name}
              </Button>
            );
          })}
        </div>
      </div>

      {formats.length > 0 && (
        <div className="bg-[#1d2230] rounded-lg p-3 text-xs text-[#8a93a3]">
          Total matches per team encounter: <span className="text-[#f5c518] font-semibold">
            {formats.reduce((s,f) => s + f.singles_count + f.doubles_count, 0)} individual matches
          </span> across {formats.length} sport{formats.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}

function FormatEditRow({ fmt, onSave, onCancel, saving }) {
  const [f, setF] = useState({ ...fmt });
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="font-semibold text-[#f0f2f7]">{f.sport_name}</p>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => onSave(f)} disabled={saving}
            className="bg-[#f5c518] text-[#0b0f18] h-7 text-xs">{saving ? 'Saving...' : 'Save'}</Button>
          <Button size="sm" variant="ghost" onClick={onCancel} className="text-[#8a93a3] h-7"><X className="h-3.5 w-3.5"/></Button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div>
          <Label className="text-[#8a93a3] text-xs">Singles</Label>
          <Input type="number" min="0" max="20" value={f.singles_count}
            onChange={e => setF({...f, singles_count:parseInt(e.target.value)||0})}
            className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] h-8 text-sm mt-1"/>
        </div>
        <div>
          <Label className="text-[#8a93a3] text-xs">Doubles</Label>
          <Input type="number" min="0" max="20" value={f.doubles_count}
            onChange={e => setF({...f, doubles_count:parseInt(e.target.value)||0})}
            className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] h-8 text-sm mt-1"/>
        </div>
        <div>
          <Label className="text-[#8a93a3] text-xs flex items-center gap-1">
            <span className="text-[#f5c518]">★</span> Platinum Players
          </Label>
          <Input type="number" min="0" max="20" value={f.platinum_count ?? 0}
            onChange={e => setF({...f, platinum_count:parseInt(e.target.value)||0})}
            className="bg-[#1d2230] border-[rgba(245,197,24,0.4)] text-[#f5c518] h-8 text-sm mt-1 font-semibold"/>
        </div>
        <div>
          <Label className="text-[#8a93a3] text-xs">Win pts</Label>
          <Input type="number" min="0" max="5" value={f.win_pts}
            onChange={e => setF({...f, win_pts:parseInt(e.target.value)||0})}
            className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] h-8 text-sm mt-1"/>
        </div>
      </div>

      {/* Scoring Rules */}
      <div className="bg-[#1d2230] rounded-lg p-3">
        <p className="text-xs font-semibold text-[#8a93a3] mb-2 uppercase tracking-wide">Scoring Rules</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <Label className="text-[#8a93a3] text-xs">Points per Game</Label>
            <Input type="number" min="1" max="100" value={f.game_points ?? 11}
              onChange={e => setF({...f, game_points:parseInt(e.target.value)||11})}
              className="bg-[#161a26] border-[#252a37] text-[#f5c518] h-8 text-sm mt-1 font-semibold"/>
          </div>
          <div>
            <Label className="text-[#8a93a3] text-xs">Games (Group)</Label>
            <Input type="number" min="1" max="9" value={f.games_per_match ?? 1}
              onChange={e => setF({...f, games_per_match:parseInt(e.target.value)||1})}
              className="bg-[#161a26] border-[#252a37] text-[#f0f2f7] h-8 text-sm mt-1"
              title="Best of N games in group stage"/>
          </div>
          <div>
            <Label className="text-[#8a93a3] text-xs">Games (KO/Semis/Final)</Label>
            <Input type="number" min="1" max="9" value={f.ko_games_per_match ?? f.games_per_match ?? 1}
              onChange={e => setF({...f, ko_games_per_match:parseInt(e.target.value)||1})}
              className="bg-[#161a26] border-[rgba(245,197,24,0.4)] text-[#f5c518] h-8 text-sm mt-1 font-semibold"
              title="Best of N games in knockout rounds"/>
          </div>
          <div>
            <Label className="text-[#8a93a3] text-xs">Min Point Diff</Label>
            <Input type="number" min="0" max="5" value={f.min_diff ?? 2}
              onChange={e => setF({...f, min_diff:parseInt(e.target.value)||0})}
              className="bg-[#161a26] border-[#252a37] text-[#f0f2f7] h-8 text-sm mt-1"
              title="Minimum winning margin (e.g. 2 for deuce)"/>
          </div>
        </div>
        <p className="text-[10px] text-[#8a93a3] mt-2">
          TT: 11 pts · 3 games group / 5 games KO · min diff 2 &nbsp;|&nbsp;
          Badminton: 21 pts · 1 game group / 3 games KO · min diff 2 &nbsp;|&nbsp;
          Pickleball: 15 pts · 1 game · min diff 2
        </p>
      </div>

      {/* Player match limits */}
      <div className="bg-[rgba(245,197,24,0.06)] rounded-lg p-3 border border-[rgba(245,197,24,0.2)]">
        <p className="text-xs font-semibold text-[#f5c518] mb-2 uppercase tracking-wide">Player Match Limits per Tie</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[#8a93a3] text-xs">Min Matches per Player</Label>
            <Input type="number" min="0" max="10" value={f.min_matches_per_player ?? 1}
              onChange={e => setF({...f, min_matches_per_player:parseInt(e.target.value)||0})}
              className="bg-[#161a26] border-[#252a37] text-[#f0f2f7] h-8 text-sm mt-1"
              title="Minimum matches each player must play in one tie"/>
          </div>
          <div>
            <Label className="text-[#8a93a3] text-xs">Max Matches per Player</Label>
            <Input type="number" min="1" max="10" value={f.max_matches_per_player ?? 2}
              onChange={e => setF({...f, max_matches_per_player:parseInt(e.target.value)||2})}
              className="bg-[rgba(245,197,24,0.1)] border-[rgba(245,197,24,0.4)] text-[#f5c518] h-8 text-sm mt-1 font-semibold"
              title="Maximum matches a player can play in one tie"/>
          </div>
        </div>
        <p className="text-[10px] text-[#8a93a3] mt-1.5">CBL Badminton rule: min 2 · max 2 per player per tie</p>
      </div>


      {(f.platinum_count > 0) && (
        <p className="text-xs text-[#8a93a3] mt-1">
          <span className="text-[#f5c518] font-medium">★ {f.platinum_count} platinum players/team</span>
          {f.singles_count > 0 && <span> → {Math.min(f.platinum_count, f.singles_count)} platinum singles, {f.singles_count - Math.min(f.platinum_count, f.singles_count)} regular singles</span>}
          {f.doubles_count > 0 && <span> → {Math.min(Math.floor(f.platinum_count/2), f.doubles_count)} platinum doubles, {f.doubles_count - Math.min(Math.floor(f.platinum_count/2), f.doubles_count)} regular doubles</span>}
        </p>
      )}
        <div className="flex flex-col justify-end">
          <label className="flex items-center gap-2 cursor-pointer mt-auto">
            <div onClick={() => setF({...f, super_match_on_tie: !f.super_match_on_tie})}
              className={`w-8 h-4 rounded-full transition-colors relative ${f.super_match_on_tie ? 'bg-[#f5c518]' : 'bg-[#252a37]'}`}>
              <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform ${f.super_match_on_tie ? 'translate-x-4' : 'translate-x-0.5'}`}/>
            </div>
            <span className="text-[#8a93a3] text-xs">Super match</span>
          </label>
        </div>
      </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Groups Tab
// ──────────────────────────────────────────────────────────────
export function GroupsTab({ tournamentId, orgSlug, orgTeams, admin = false }) {
  const [groups, setGroups]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [creating, setCreating]   = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null); // group object
  const [assignSelected, setAssignSelected] = useState([]);

  useEffect(() => { fetchGroups(); }, [tournamentId]);

  const fetchGroups = async () => {
    setLoading(true);
    const token = localStorage.getItem('btccp_access_token');
    const res = await fetch(`/api/orgs/${orgSlug}/tournaments/${tournamentId}/groups`,
      { headers: { 'Authorization': `Bearer ${token}` } });
    const data = await res.json();
    setGroups(Array.isArray(data) ? data : []);
    setLoading(false);
  };

  const createGroup = async (letter) => {
    if (groups.find(g => g.letter === letter)) { toast.error(`Group ${letter} already exists`); return; }
    setCreating(true);
    const token = localStorage.getItem('btccp_access_token');
    const res = await fetch(`/api/orgs/${orgSlug}/tournaments/${tournamentId}/groups`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ letter }),
    });
    if (res.ok) { fetchGroups(); toast.success(`Group ${letter} created`); }
    else { const d=await res.json(); toast.error(d.detail||'Failed'); }
    setCreating(false);
  };

  const deleteGroup = async (gid, letter) => {
    if (!confirm(`Delete Group ${letter} and all its matches?`)) return;
    const token = localStorage.getItem('btccp_access_token');
    await fetch(`/api/orgs/${orgSlug}/tournaments/${tournamentId}/groups/${gid}`, {
      method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` },
    });
    fetchGroups();
    toast.success(`Group ${letter} deleted`);
  };

  const openAssign = (grp) => {
    setAssignTarget(grp);
    setAssignSelected(grp.team_ids || []);
    setAssignOpen(true);
  };

  const saveAssignment = async () => {
    const token = localStorage.getItem('btccp_access_token');
    const res = await fetch(`/api/orgs/${orgSlug}/tournaments/${tournamentId}/groups/${assignTarget.id}/teams`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ team_ids: assignSelected }),
    });
    if (res.ok) {
      toast.success(`${assignSelected.length} teams assigned to Group ${assignTarget.letter}`);
      setAssignOpen(false);
      fetchGroups();
    }
  };

  const generateEncounters = async () => {
    if (!confirm('This will delete existing encounters and regenerate all group matches. Continue?')) return;
    setGenLoading(true);
    const token = localStorage.getItem('btccp_access_token');
    const res = await fetch(`/api/orgs/${orgSlug}/tournaments/${tournamentId}/generate-encounters`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    const data = await res.json();
    if (res.ok) toast.success(`${data.created} encounters generated!`);
    else { const d=await (res.json?.() || {}); toast.error(d.detail || 'Failed'); }
    setGenLoading(false);
  };

  const availableLetters = ['A','B','C','D'].filter(l => !groups.find(g => g.letter===l));
  // Teams not yet assigned to any group
  const assignedTeamIds = new Set(groups.flatMap(g => g.team_ids || []));
  const unassigned = orgTeams.filter(t => !assignedTeamIds.has(t.id));

  return (
    <div className="space-y-4" data-testid="groups-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold text-[#f0f2f7]">Groups</h3>
          <p className="text-sm text-[#8a93a3]">Create groups and manually assign teams on tournament day</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {availableLetters.slice(0,2).map(l => (
            <Button key={l} size="sm" variant="outline" onClick={() => createGroup(l)} disabled={creating}
              className="border-[#252a37] text-[#f0f2f7] hover:bg-[#161a26] text-xs"
              style={{ borderColor: GROUP_COLORS[l]+'80', color: GROUP_COLORS[l] }}>
              <Plus className="h-3.5 w-3.5 mr-1"/> Add Group {l}
            </Button>
          ))}
          {groups.length >= 2 && (
            <Button size="sm" onClick={generateEncounters} disabled={genLoading}
              className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a] text-xs"
              data-testid="generate-encounters-button">
              <Zap className="h-3.5 w-3.5 mr-1"/>
              {genLoading ? 'Generating...' : 'Generate Matches'}
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[1,2].map(i=><Skeleton key={i} className="h-32 bg-[#1d2230] rounded-xl"/>)}
        </div>
      ) : groups.length === 0 ? (
        <Card className="bg-[#161a26] border-[#252a37]">
          <CardContent className="p-8 text-center">
            <Users className="h-12 w-12 text-[#8a93a3] mx-auto mb-3"/>
            <p className="text-[#f0f2f7] font-medium mb-1">No groups yet</p>
            <p className="text-sm text-[#8a93a3]">Create Group A and Group B to get started</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {groups.map(grp => (
            <Card key={grp.id} className="bg-[#161a26] border-[#252a37]"
              style={{ borderLeftWidth: 3, borderLeftColor: GROUP_COLORS[grp.letter] || '#252a37' }}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-lg flex items-center justify-center font-bold text-white text-sm"
                      style={{ backgroundColor: GROUP_COLORS[grp.letter] || '#252a37' }}>
                      {grp.letter}
                    </div>
                    <div>
                      <p className="font-semibold text-[#f0f2f7]">{grp.name}</p>
                      <p className="text-xs text-[#8a93a3]">{(grp.team_ids||[]).length} teams</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => openAssign(grp)}
                      className="border-[#252a37] text-[#f0f2f7] hover:bg-[#1d2230] text-xs h-7"
                      data-testid={`assign-teams-group-${grp.letter}`}>
                      <Users className="h-3 w-3 mr-1"/> Assign Teams
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteGroup(grp.id, grp.letter)}
                      className="text-[#8a93a3] hover:text-[#ef4444] h-7 px-2"><Trash2 className="h-3.5 w-3.5"/></Button>
                  </div>
                </div>
                {(grp.teams||[]).length > 0 ? (
                  <div className="space-y-1.5">
                    {grp.teams.map((t, idx) => (
                      <div key={t.id} className="flex items-center gap-2 py-1 px-2 bg-[#1d2230] rounded-lg">
                        <span className="text-xs text-[#8a93a3] w-5">{grp.letter}{idx+1}</span>
                        <div className="h-5 w-5 rounded flex items-center justify-center text-[#f5c518] text-[10px] font-bold flex-shrink-0"
                          style={{ backgroundColor: t.color || '#1d2230' }}>
                          {t.short_name?.[0] || t.name?.[0]}
                        </div>
                        <span className="text-sm text-[#f0f2f7] truncate">{t.name}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-[#8a93a3] text-center py-2">No teams assigned yet</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {unassigned.length > 0 && groups.length > 0 && (
        <div>
          <p className="text-xs text-[#8a93a3] uppercase tracking-wider mb-2">Unassigned Teams ({unassigned.length})</p>
          <div className="flex flex-wrap gap-2">
            {unassigned.map(t => (
              <div key={t.id} className="bg-[#161a26] border border-[#252a37] rounded-lg px-3 py-1.5 text-sm text-[#8a93a3] opacity-60">
                {t.name}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Assign Teams Dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="bg-[#161a26] border-[#252a37] max-w-sm" data-testid="assign-teams-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7]">
              Assign Teams to Group {assignTarget?.letter}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[300px] bg-[#1d2230] rounded-lg border border-[#252a37]">
            {orgTeams.map(t => {
              const selected = assignSelected.includes(t.id);
              return (
                <div key={t.id} onClick={() => setAssignSelected(prev =>
                    prev.includes(t.id) ? prev.filter(x=>x!==t.id) : [...prev,t.id]
                  )}
                  className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer border-b border-[#252a37]/50 last:border-0 transition-colors ${selected?'bg-[rgba(245,197,24,0.06)]':'hover:bg-[#252a37]/50'}`}>
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${selected?'bg-[#f5c518] border-[#f5c518]':'border-[#8a93a3]'}`}>
                    {selected && <Check className="h-3 w-3 text-[#0b0f18]"/>}
                  </div>
                  <span className={`text-sm ${selected?'text-[#f0f2f7] font-medium':'text-[#8a93a3]'}`}>{t.name}</span>
                </div>
              );
            })}
          </ScrollArea>
          <p className="text-xs text-[#8a93a3]">{assignSelected.length} teams selected</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)} className="border-[#252a37] text-[#f0f2f7]">Cancel</Button>
            <Button onClick={saveAssignment} className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]">
              Confirm Assignment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Encounters Tab
// ──────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────
// Match Detail Dialog
// ──────────────────────────────────────────────────────────────
const SLOT_COLORS = { P1:'#f5c518', P2:'#f5c518', R1:'#3b82f6', R2:'#3b82f6', R3:'#3b82f6', R4:'#8b5cf6', R5:'#8b5cf6' };

function MatchDetailDialog({ enc, orgSlug, onClose }) {
  const [rosters, setRosters]       = useState({ home:[], away:[] });
  const [editLog, setEditLog]       = useState(null);  // null=not loaded, []= no edits
  const [viewEdit, setViewEdit]     = useState(null);  // edit entry to compare
  const [showLog, setShowLog]       = useState(false);

  useEffect(() => {
    if (!enc) return;
    const token = localStorage.getItem('btccp_access_token');
    fetch(`/api/orgs/${orgSlug}/encounters/${enc.id}/team-rosters`,
      { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : {})
      .then(d => setRosters(d || { home:[], away:[] }))
      .catch(() => {});
    // Fetch edit log if encounter has been edited
    if (enc.has_edits || enc.scored_by_name) {
      fetch(`/api/orgs/${orgSlug}/tournaments/${enc.tournament_id}/encounters/${enc.id}/edit-log`,
        { headers: { 'Authorization': `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : null)
        .then(d => d && setEditLog(d))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enc?.id]);

  if (!enc) return null;

  const playerName = (pid, side) => {
    if (!pid) return '—';
    const list = rosters[side] || [];
    const p = list.find(r => r.id === pid || r.player_id === pid);
    return p?.full_name || p?.player?.full_name || p?.player_name || pid.slice(-6);
  };

  const fmtDt = (iso) => iso ? new Date(iso).toLocaleString('en-IN',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'}) : '—';
  const SPORT_ICONS_DLG = { table_tennis:'🏓', badminton:'🏸', chess:'♟️', carrom:'🎯', pickleball:'🥒', squash:'🎾', cricket:'🏏', football:'⚽', default:'🏅' };

  return (
    <Dialog open={!!enc} onOpenChange={onClose}>
      <DialogContent className="bg-[#161a26] border-[#252a37] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="match-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#f0f2f7] flex items-center gap-2">
            <Trophy className="h-5 w-5 text-[#f5c518]"/>
            Match Detail
            {enc.has_edits && (
              <Badge className="ml-2 bg-[rgba(245,197,24,0.15)] text-[#f5c518] border border-[rgba(245,197,24,0.3)] text-[10px]">
                Edited {enc.edit_count > 1 ? `(${enc.edit_count}×)` : ''}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Teams header */}
        <div className="flex items-center justify-between gap-4 py-3 px-4 bg-[#10131c] rounded-xl border border-[#252a37]">
          <div className="flex items-center gap-3 flex-1">
            <div className="h-10 w-10 rounded-xl flex items-center justify-center font-bold text-white text-sm flex-shrink-0 overflow-hidden"
              style={{ backgroundColor: enc.home_team?.color || '#3b82f6' }}>
              {enc.home_team?.logo_url ? <img src={enc.home_team.logo_url} className="h-full w-full object-cover" alt=""/> : enc.home_team?.short_name?.[0] || 'H'}
            </div>
            <div>
              <p className={`font-bold text-base ${enc.winner==='home'?'text-[#f5c518]':'text-[#f0f2f7]'}`}>{enc.home_team?.name || 'Home'}</p>
              {enc.winner==='home' && <p className="text-xs text-[#f5c518]">Winner 🏆</p>}
            </div>
          </div>
          <div className="text-center flex-shrink-0">
            <p className="text-2xl font-black tabular-nums text-[#f5c518]">{enc.total_home_pts ?? '–'} : {enc.total_away_pts ?? '–'}</p>
            <p className="text-[10px] text-[#8a93a3] uppercase tracking-wide">Total Points</p>
          </div>
          <div className="flex items-center gap-3 flex-1 justify-end text-right">
            <div>
              <p className={`font-bold text-base ${enc.winner==='away'?'text-[#f5c518]':'text-[#f0f2f7]'}`}>{enc.away_team?.name || 'Away'}</p>
              {enc.winner==='away' && <p className="text-xs text-[#f5c518]">Winner 🏆</p>}
            </div>
            <div className="h-10 w-10 rounded-xl flex items-center justify-center font-bold text-white text-sm flex-shrink-0 overflow-hidden"
              style={{ backgroundColor: enc.away_team?.color || '#ef4444' }}>
              {enc.away_team?.logo_url ? <img src={enc.away_team.logo_url} className="h-full w-full object-cover" alt=""/> : enc.away_team?.short_name?.[0] || 'A'}
            </div>
          </div>
        </div>

        {/* Per-sport breakdown */}
        <div className="space-y-4 mt-2">
          {(enc.sport_results || []).map(sr => (
            <div key={sr.sport_code}>
              {/* Sport header */}
              <div className="flex items-center justify-between px-3 py-2 bg-[#1d2230] rounded-t-lg border border-[#252a37]">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{SPORT_ICONS_DLG[sr.sport_code] || SPORT_ICONS_DLG.default}</span>
                  <span className="font-semibold text-[#f0f2f7] capitalize">{sr.sport_code.replace(/_/g,' ')}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <span className={`font-bold ${sr.sport_winner==='home'?'text-[#22c55e]':'text-[#8a93a3]'}`}>{sr.home_match_wins ?? 0}W</span>
                  <span className="text-[#8a93a3]">vs</span>
                  <span className={`font-bold ${sr.sport_winner==='away'?'text-[#22c55e]':'text-[#8a93a3]'}`}>{sr.away_match_wins ?? 0}W</span>
                  {sr.sport_winner && sr.sport_winner !== 'tie_pending' && (
                    <Badge className={`text-[10px] ml-1 ${sr.sport_winner==='draw'?'bg-[rgba(245,197,24,0.15)] text-[#f5c518]':sr.sport_winner==='home'?'bg-[rgba(34,197,94,0.15)] text-[#22c55e]':'bg-[rgba(239,68,68,0.15)] text-[#ef4444]'}`}>
                      {sr.sport_winner === 'draw' ? 'Draw' : sr.sport_winner === 'home' ? `${enc.home_team?.short_name || 'Home'} wins` : `${enc.away_team?.short_name || 'Away'} wins`}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Match rows */}
              <div className="border border-t-0 border-[#252a37] rounded-b-lg overflow-hidden">
                {(sr.matches || []).map((m, i) => {
                  const homeName = playerName(m.home_player_id, 'home');
                  const awayName = playerName(m.away_player_id, 'away');
                  const slotColor = SLOT_COLORS[m.slot] || '#8a93a3';
                  return (
                    <div key={m.slot||m.label||i} className={`flex items-center gap-2 px-3 py-2.5 border-b border-[#252a37]/50 last:border-0 ${m.winner ? 'hover:bg-[#10131c]' : ''} text-sm`}>
                      {/* Slot */}
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0"
                        style={{ backgroundColor: `${slotColor}20`, color: slotColor }}>{m.slot}</span>
                      {/* Home player */}
                      <span className={`flex-1 truncate ${m.winner==='home'?'text-[#22c55e] font-semibold':'text-[#f0f2f7]'}`}>{homeName}</span>
                      {/* Score */}
                      <div className="flex-shrink-0 text-center min-w-[80px]">
                        {m.winner ? (
                          <span className="font-bold tabular-nums text-[#f5c518]">
                            {m.home_score ?? '–'} – {m.away_score ?? '–'}
                          </span>
                        ) : (
                          <span className="text-[#8a93a3] text-xs">not played</span>
                        )}
                      </div>
                      {/* Away player */}
                      <span className={`flex-1 truncate text-right ${m.winner==='away'?'text-[#22c55e] font-semibold':'text-[#f0f2f7]'}`}>{awayName}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Points awarded summary */}
        {enc.winner && (
          <div className="mt-4 bg-[#10131c] rounded-xl border border-[#252a37] p-4">
            <p className="text-xs font-semibold text-[#8a93a3] uppercase tracking-wider mb-3">Points Awarded</p>
            <div className="space-y-2">
              {(enc.sport_results || []).filter(sr => sr.sport_winner && sr.sport_winner !== 'tie_pending').map(sr => (
                <div key={sr.sport_code} className="flex items-center justify-between text-sm">
                  <span className="text-[#8a93a3] capitalize">{sr.sport_code.replace(/_/g,' ')}</span>
                  <div className="flex items-center gap-3">
                    <span className={`font-semibold ${sr.home_pts > 0 ? 'text-[#22c55e]' : 'text-[#8a93a3]'}`}>{sr.home_pts ?? 0}</span>
                    <span className="text-[#8a93a3]">vs</span>
                    <span className={`font-semibold ${sr.away_pts > 0 ? 'text-[#22c55e]' : 'text-[#8a93a3]'}`}>{sr.away_pts ?? 0}</span>
                  </div>
                </div>
              ))}
              <div className="border-t border-[#252a37] pt-2 flex items-center justify-between font-bold">
                <span className="text-[#f0f2f7]">Total</span>
                <div className="flex items-center gap-3">
                  <span className="text-[#f5c518] text-lg">{enc.total_home_pts ?? 0}</span>
                  <span className="text-[#8a93a3]">vs</span>
                  <span className="text-[#f5c518] text-lg">{enc.total_away_pts ?? 0}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Scorer info + Edit Log */}
        <div className="mt-4 space-y-2">
          {/* Scored by */}
          {(enc.scored_by_name || editLog?.scored_by) && (
            <div className="flex items-center justify-between px-3 py-2 bg-[#10131c] rounded-lg border border-[#252a37] text-xs">
              <span className="text-[#8a93a3]">Scored by</span>
              <div className="flex items-center gap-2">
                <span className="text-[#f0f2f7] font-medium">{enc.scored_by_name || editLog?.scored_by}</span>
                {(enc.scored_at || editLog?.scored_at) && (
                  <span className="text-[#8a93a3]">{fmtDt(enc.scored_at || editLog?.scored_at)}</span>
                )}
              </div>
            </div>
          )}

          {/* Edit log */}
          {enc.has_edits && editLog && editLog.edit_log?.length > 0 && (
            <div className="bg-[#10131c] rounded-lg border border-[rgba(245,197,24,0.25)] overflow-hidden">
              <button onClick={() => setShowLog(!showLog)}
                className="w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-[#161a26] transition-colors">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] bg-[rgba(245,197,24,0.15)] text-[#f5c518] px-2 py-0.5 rounded-full font-semibold">
                    {editLog.edit_log.length} Edit{editLog.edit_log.length > 1 ? 's' : ''}
                  </span>
                  <span className="text-[#8a93a3]">Score sheet was modified</span>
                </div>
                <span className="text-[#8a93a3]">{showLog ? '▲' : '▼'} View Log</span>
              </button>
              {showLog && (
                <div className="border-t border-[#252a37]">
                  {editLog.edit_log.map((entry) => (
                    <div key={entry.id} className="flex items-center justify-between px-3 py-2.5 border-b border-[#252a37]/50 last:border-0 hover:bg-[#161a26] transition-colors">
                      <div>
                        <p className="text-xs text-[#f0f2f7] font-medium">{entry.editor_name}</p>
                        <p className="text-[10px] text-[#8a93a3]">{fmtDt(entry.edited_at)}{entry.reason ? ` — ${entry.reason}` : ''}</p>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => setViewEdit(viewEdit?.id === entry.id ? null : entry)}
                        className="border-[#252a37] text-[#f5c518] hover:bg-[rgba(245,197,24,0.08)] h-7 text-[10px]">
                        {viewEdit?.id === entry.id ? 'Hide' : 'Compare'}
                      </Button>
                    </div>
                  ))}
                  {/* Comparison view */}
                  {viewEdit && (
                    <div className="border-t border-[#252a37] p-3 bg-[#0f1219]">
                      <p className="text-xs font-semibold text-[#f5c518] mb-3">Score Comparison — {fmtDt(viewEdit.edited_at)}</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-[10px] font-bold text-[#8a93a3] uppercase mb-2">Original</p>
                          {(viewEdit.before || []).map(sr => (
                            <div key={sr.sport_code} className="mb-2">
                              <p className="text-[10px] text-[#f5c518] mb-1 capitalize">{sr.sport_code.replace(/_/g,' ')}</p>
                              {(sr.matches||[]).map((m,i) => (
                                <div key={m.slot||m.label||i} className="text-[10px] text-[#8a93a3] flex gap-2">
                                  <span>{m.slot}</span>
                                  <span>{m.home_score ?? '—'} – {m.away_score ?? '—'}</span>
                                  {m.winner && <span className="text-[#22c55e]">({m.winner})</span>}
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                        <div>
                          <p className="text-[10px] font-bold text-[#f5c518] uppercase mb-2">Revised</p>
                          {(viewEdit.after || []).map(sr => (
                            <div key={sr.sport_code} className="mb-2">
                              <p className="text-[10px] text-[#f5c518] mb-1 capitalize">{sr.sport_code.replace(/_/g,' ')}</p>
                              {(sr.matches||[]).map((m,i) => (
                                <div key={m.slot||m.label||i} className="text-[10px] text-[#f0f2f7] flex gap-2 font-medium">
                                  <span>{m.slot}</span>
                                  <span>{m.home_score ?? '—'} – {m.away_score ?? '—'}</span>
                                  {m.winner && <span className="text-[#22c55e]">({m.winner})</span>}
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#252a37] text-[#f0f2f7]">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function EncountersTab({ tournamentId, orgSlug }) {
  const [encounters, setEncounters] = useState([]);
  const [sportFormats, setSportFormats] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [scoreTarget, setScoreTarget] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);
  const [scheduleTarget, setScheduleTarget] = useState(null);
  const [schedForm, setSchedForm]   = useState({ scheduled_at: '', venue: '' });
  const [filterGroup, setFilterGroup] = useState('all');
  const [filterSport, setFilterSport] = useState('all');
  const [expandedEnc, setExpandedEnc] = useState(null); // expanded encounter id
  // Edit teams override for KO encounters
  const [editTeamsTarget, setEditTeamsTarget] = useState(null);
  const [editTeamsForm, setEditTeamsForm] = useState({ home_team_id: '', away_team_id: '' });
  const [orgTeams, setOrgTeams] = useState([]);
  const [savingTeams, setSavingTeams] = useState(false);
  // Lineup builder (pre-match pairing)
  const [lineupTarget, setLineupTarget] = useState(null); // encounter
  const [lineupRosters, setLineupRosters] = useState({ home: [], away: [] });
  const [lineupSlots, setLineupSlots] = useState([]); // [{combo, home1, home2, away1, away2}]
  const [savingLineup, setSavingLineup] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchEncounters(); fetchSportFormats(); }, [tournamentId]);

  const fetchEncounters = async () => {
    setLoading(true);
    const token = localStorage.getItem('btccp_access_token');
    const res = await fetch(`/api/orgs/${orgSlug}/tournaments/${tournamentId}/encounters`,
      { headers: { 'Authorization': `Bearer ${token}` } });
    const data = await res.json();
    setEncounters(Array.isArray(data) ? data : []);
    setLoading(false);
  };

  const fetchSportFormats = async () => {
    const token = localStorage.getItem('btccp_access_token');
    const res = await fetch(`/api/orgs/${orgSlug}/tournaments/${tournamentId}/sport-formats`,
      { headers: { 'Authorization': `Bearer ${token}` } });
    const data = await res.json();
    setSportFormats(Array.isArray(data) ? data : []);
  };

  // COMBO ORDER for 5-doubles: CC AA CC AC AC
  const COMBO_ORDERS = {5: ['CC','AA','CC','AC','AC'], 3: ['CC','AA','AC'], 1: ['CC']};
  const COMBO_LABELS = { CC:'Champion + Champion', AA:'Ace + Ace', AC:'Ace + Champion' };

  const openLineup = async (enc) => {
    const token = localStorage.getItem('btccp_access_token');
    // Fetch rosters + sport format (for max_matches_per_player)
    const [hr, ar, sfr] = await Promise.allSettled([
      fetch(`/api/orgs/${orgSlug}/teams/${enc.home_team_id}/roster`, { headers: { Authorization: `Bearer ${token}` } }),
      fetch(`/api/orgs/${orgSlug}/teams/${enc.away_team_id}/roster`, { headers: { Authorization: `Bearer ${token}` } }),
      fetch(`/api/orgs/${orgSlug}/tournaments/${tournamentId}/sport-formats`, { headers: { Authorization: `Bearer ${token}` } }),
    ]);
    const homeRoster = hr.status==='fulfilled' && hr.value.ok ? await hr.value.json() : [];
    const awayRoster = ar.status==='fulfilled' && ar.value.ok ? await ar.value.json() : [];
    const allFmts    = sfr.status==='fulfilled' && sfr.value.ok ? await sfr.value.json() : [];

    // Attach sport format max to encounter for use in dialog
    const sr = (enc.sport_results||[])[0] || {};
    const fmt = allFmts.find(f => f.sport_code === sr.sport_code) || {};
    enc._maxPerPlayer = fmt.max_matches_per_player || 99;
    enc._minPerPlayer = fmt.min_matches_per_player || 1;

    setLineupRosters({ home: homeRoster, away: awayRoster });
    const matches = sr.matches || [];
    const totalM = matches.length;
    const comboOrder = COMBO_ORDERS[totalM] || matches.map((_,i) => i===1?'AA':i>=3?'AC':'CC');
    const slots = matches.map((m, i) => ({
      combo: comboOrder[i] || 'CC',
      home1: m.home_player || '', home2: m.home_player2 || '',
      away1: m.away_player || '', away2: m.away_player2 || '',
      slot: m.slot || m.label || `M${i+1}`,
    }));
    setLineupSlots(slots);
    setLineupTarget(enc);
  };

  const saveLineup = async () => {
    if (!lineupTarget) return;
    setSavingLineup(true);
    try {
      const token = localStorage.getItem('btccp_access_token');
      const sr = (lineupTarget.sport_results||[])[0];
      if (!sr) { toast.error('No sport found'); return; }
      const updatedMatches = (sr.matches||[]).map((m, i) => {
        const s = lineupSlots[i] || {};
        return { ...m, home_player: s.home1||'', home_player2: s.home2||'', away_player: s.away1||'', away_player2: s.away2||'' };
      });
      const res = await fetch(`/api/orgs/${orgSlug}/scorer/encounters/${lineupTarget.id}/sport/${sr.sport_code}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ matches: updatedMatches }),
      });
      if (res.ok) { toast.success('Lineup saved!'); setLineupTarget(null); fetchEncounters(); }
      else { const d = await res.json(); toast.error(d.detail || 'Failed to save lineup'); }
    } finally { setSavingLineup(false); }
  };

  const openEditTeams = async (enc) => {
    setEditTeamsForm({ home_team_id: enc.home_team_id || '', away_team_id: enc.away_team_id || '' });
    setEditTeamsTarget(enc);
    if (orgTeams.length === 0) {
      const token = localStorage.getItem('btccp_access_token');
      const res = await fetch(`/api/orgs/${orgSlug}/teams`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setOrgTeams(Array.isArray(data) ? data : []);
    }
  };

  const saveEditTeams = async () => {
    if (!editTeamsTarget) return;
    setSavingTeams(true);
    try {
      const token = localStorage.getItem('btccp_access_token');
      const res = await fetch(`/api/orgs/${orgSlug}/tournaments/${tournamentId}/encounters/${editTeamsTarget.id}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          home_team_id: editTeamsForm.home_team_id || null,
          away_team_id: editTeamsForm.away_team_id || null,
        }),
      });
      if (res.ok) {
        toast.success('Teams updated for knockout match');
        setEditTeamsTarget(null);
        fetchEncounters();
      } else {
        const d = await res.json();
        toast.error(d.detail || 'Failed to update teams');
      }
    } finally {
      setSavingTeams(false);
    }
  };

  const saveSchedule = async () => {
    const token = localStorage.getItem('btccp_access_token');
    const res = await fetch(`/api/orgs/${orgSlug}/tournaments/${tournamentId}/encounters/${scheduleTarget.id}`, {
      method: 'PATCH',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ scheduled_at: schedForm.scheduled_at || null, venue: schedForm.venue || null }),
    });
    if (res.ok) { toast.success('Schedule saved'); setScheduleTarget(null); fetchEncounters(); }
  };

  const groups = [...new Set(encounters.map(e => e.group_letter))].sort();
  const SPORT_ICONS_ENC = {
    table_tennis: '🏓', badminton: '🏸', chess: '♟️',
    carrom: '🎯', pickleball: '🥒', squash: '🎾', default: '🏅'
  };

  let filtered = filterGroup === 'all' ? encounters : encounters.filter(e => e.group_letter === filterGroup);
  if (filterSport !== 'all') filtered = filtered.filter(e => e.sport_results?.some(sr => sr.sport_code === filterSport));

  const groupedBySection = filtered.reduce((acc, enc) => {
    const key = `Group ${enc.group_letter}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(enc);
    return acc;
  }, {});

  const TeamDot = ({ team, side, pts, winner, seed }) => {
    const isWinner = winner === side;
    const initials    = team?.name?.[0] || seed?.[0] || '?';
    const bgColor     = team?.color || (seed ? '#4b5563' : '#3b82f6');
    return (
      <div className={`flex items-center gap-2 flex-1 min-w-0 ${side==='away' ? 'justify-end flex-row-reverse' : ''}`}>
        <div className="h-8 w-8 rounded-full flex items-center justify-center font-bold text-white text-xs flex-shrink-0 overflow-hidden"
          style={{ backgroundColor: bgColor }}>
          {team?.logo_url?.startsWith('data:')
            ? <img src={team.logo_url} className="h-full w-full object-cover" alt=""/>
            : team?.short_name?.[0] || initials}
        </div>
        <div className={`min-w-0 ${side==='away' ? 'text-right' : ''}`}>
          <p className={`text-sm font-semibold truncate ${isWinner ? 'text-[#f5c518]' : seed && !team ? 'text-[#8a93a3] italic' : 'text-[#f0f2f7]'}`}>
            {team?.name || seed || 'TBD'}
          </p>
          {pts > 0 && <p className="text-xs text-[#8a93a3]">{pts} pts</p>}
        </div>
        {isWinner && <span className="text-[#f5c518] text-xs flex-shrink-0">🏆</span>}
      </div>
    );
  };

  const renderEncounterCard = (enc, isDone, isLive, isExpanded, isFinal) => (
    <div key={enc.id} className={`bg-[#10131c] rounded-xl border overflow-hidden ${isLive?'border-[rgba(239,68,68,0.35)]':isExpanded?'border-[rgba(245,197,24,0.3)]':isFinal?'border-[rgba(245,197,24,0.15)]':'border-[#1e2435] hover:border-[#252a37]'}`}>
      {/* Clickable header */}
      <div className="cursor-pointer" onClick={() => setExpandedEnc(isExpanded ? null : enc.id)}>
        <div className="px-4 pt-3 pb-1 flex items-center justify-between text-xs text-[#8a93a3]">
          <div className="flex items-center gap-3">
            {enc.scheduled_at && (
              <span className="flex items-center gap-1 font-medium text-[#f5c518]">
                <Clock className="h-3 w-3"/>
                {new Date(enc.scheduled_at).toLocaleDateString('en-IN',{day:'2-digit',month:'short'})}
                {' '}
                {new Date(enc.scheduled_at).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})}
              </span>
            )}
            {enc.match_label && <span className={`font-mono text-[10px] ${isFinal?'text-[#f5c518] font-bold':'text-[#8a93a3]'}`}>{enc.match_label}</span>}
            {enc.venue && <span>📍 {enc.venue}</span>}
            {isFinal && !enc.home_team?.name && <span className="text-[10px] bg-[rgba(245,197,24,0.1)] text-[#f5c518] px-2 py-0.5 rounded-full">Awaiting SF results</span>}
          </div>
          {isLive && <span className="text-[#ef4444] font-semibold flex items-center gap-1"><span className="w-1.5 h-1.5 bg-[#ef4444] rounded-full animate-pulse"/>LIVE</span>}
          {isDone && <span className="text-[#22c55e] font-medium">✓ DONE</span>}
          {enc.has_edits && <span className="text-[10px] text-[#f5c518] bg-[rgba(245,197,24,0.1)] px-1.5 py-0.5 rounded">Edited</span>}
        </div>
        <div className="px-4 py-2 flex items-center gap-3">
          <TeamDot team={enc.home_team} side="home" pts={enc.total_home_pts} winner={enc.winner} seed={enc.home_seed}/>
          <div className="text-center flex-shrink-0 px-2">
            {isDone || isLive ? (
              <p className={`text-xl font-bold tabular-nums ${isDone?'text-[#f5c518]':'text-[#f0f2f7]'}`}>{enc.total_home_pts} – {enc.total_away_pts}</p>
            ) : <p className="text-[#8a93a3] font-bold text-lg">vs</p>}
          </div>
          <TeamDot team={enc.away_team} side="away" pts={enc.total_away_pts} winner={enc.winner} seed={enc.away_seed}/>
        </div>
        <div className="px-4 pb-3 flex items-center justify-between">
          <div className="flex flex-wrap gap-1.5">
            {enc.sport_results?.filter(sr=>sr.sport_winner&&sr.sport_winner!=='tie_pending').map(sr=>(
              <span key={sr.sport_code} className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${sr.sport_winner==='home'?'bg-[rgba(34,197,94,0.12)] text-[#22c55e]':sr.sport_winner==='away'?'bg-[rgba(239,68,68,0.12)] text-[#ef4444]':'bg-[rgba(245,197,24,0.12)] text-[#f5c518]'}`}>
                {SPORT_ICONS_ENC[sr.sport_code]||'🏅'} {sr.home_match_wins}-{sr.away_match_wins}
              </span>
            ))}
          </div>
          <div className="flex gap-1.5 flex-shrink-0" onClick={e=>e.stopPropagation()}>
            <Button size="sm" variant="ghost" onClick={()=>{setScheduleTarget(enc);setSchedForm({scheduled_at:enc.scheduled_at||'',venue:enc.venue||''}); }} className="text-[#8a93a3] hover:text-[#f0f2f7] h-7 px-2"><Clock className="h-3.5 w-3.5"/></Button>
            {/* Lineup button — for encounters with doubles matches */}
            {enc.sport_results?.some(sr => sr.matches?.length > 0) && (
              <Button size="sm" variant="ghost" onClick={() => openLineup(enc)}
                className="text-[#8a93a3] hover:text-[#22c55e] h-7 px-2"
                title="Set match lineup / pairings"
                data-testid="set-lineup-button">
                <Users className="h-3.5 w-3.5"/>
              </Button>
            )}
            {['sf','final','3rd'].includes(enc.round_type) && (
              <Button size="sm" variant="ghost" onClick={()=>openEditTeams(enc)}
                className="text-[#8a93a3] hover:text-[#f5c518] h-7 px-2"
                title="Override teams for this match"
                data-testid="edit-ko-teams-button">
                <Users className="h-3.5 w-3.5"/>
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={()=>setDetailTarget(enc)} className="text-[#8a93a3] hover:text-[#f5c518] h-7 px-2" data-testid="view-encounter-detail-button"><Eye className="h-3.5 w-3.5"/></Button>
            <Button size="sm" onClick={()=>setScoreTarget(enc)} className={`h-7 text-xs px-3 ${isDone?'bg-[#1d2230] text-[#8a93a3]':'bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]'}`} data-testid="score-encounter-button">
              <Edit2 className="h-3 w-3 mr-1"/>{isDone?'Edit':'Score'}
            </Button>
          </div>
        </div>
      </div>{/* end clickable */}

      {/* Expanded per-sport match details */}
      {isExpanded && (
        <div className="border-t border-[#1e2435] px-4 py-3 space-y-3 bg-[#0a0d14]">
          {enc.sport_results?.map(sr => {
            const sc = sr.sport_code;
            const isResult = ['chess','carrom'].includes(sc);
            const ht = enc.home_team?.name || enc.home_seed || 'Home';
            const at = enc.away_team?.name || enc.away_seed || 'Away';
            return (
              <div key={sc}>
                <div className="flex items-center gap-2 mb-1.5">
                  <SportIcon sport={sc} size={16}/>
                  <span className="text-xs font-bold text-[#f0f2f7] capitalize">{sc.replace(/_/g,' ')}</span>
                  {sr.sport_winner && (
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${sr.sport_winner==='home'?'bg-[rgba(34,197,94,0.12)] text-[#22c55e]':sr.sport_winner==='away'?'bg-[rgba(239,68,68,0.12)] text-[#ef4444]':'bg-[rgba(245,197,24,0.12)] text-[#f5c518]'}`}>
                      {sr.sport_winner==='home'?ht:sr.sport_winner==='away'?at:'Draw'} wins {sr.home_match_wins}–{sr.away_match_wins}
                    </span>
                  )}
                  <span className="text-[10px] text-[#f5c518] ml-auto font-bold">{sr.home_pts||0} – {sr.away_pts||0} pts</span>
                </div>
                <div className="grid gap-1">
                  {sr.matches?.map((m,mi) => {
                    if (!m.winner) return null;
                    const hw = m.winner==='home'; const aw = m.winner==='away'; const dd = m.winner==='draw';
                    const hp = m.home_player || (m.home_players?.[0]) || ht;
                    const ap = m.away_player || (m.away_players?.[0]) || at;
                    const games = (m.games||[]).filter(g=>g.home||g.away);
                    const scoreStr = isResult
                      ? (dd?'Draw':hw?'Win':'Loss')
                      : (sc==='table_tennis' && games.length>0
                          ? games.map(g=>`${g.home}–${g.away}`).join(' ')
                          : (m.home_score!=null?`${m.home_score}–${m.away_score}`:''));
                    return (
                      <div key={mi} className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-[#10131c] border border-[#1e2435] text-xs">
                        <span className="text-[9px] font-bold text-[#8a93a3] w-5 flex-shrink-0">{m.slot||`M${mi+1}`}</span>
                        <span className={`flex-1 truncate ${hw?'text-[#22c55e] font-semibold':aw?'text-[#ef4444]':'text-[#8a93a3]'}`}>{hp}</span>
                        <span className={`font-black tabular-nums text-[11px] flex-shrink-0 ${hw?'text-[#22c55e]':aw?'text-[#ef4444]':dd?'text-[#f5c518]':'text-[#8a93a3]'}`}>{scoreStr}</span>
                        <span className={`flex-1 truncate text-right ${aw?'text-[#22c55e] font-semibold':hw?'text-[#ef4444]':'text-[#8a93a3]'}`}>{ap}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="encounters-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-semibold text-[#f0f2f7]">Fixtures</h3>
          <p className="text-sm text-[#8a93a3]">{encounters.length} matches</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={fetchEncounters} className="text-[#8a93a3]"><RefreshCw className="h-4 w-4"/></Button>
          <select value={filterGroup} onChange={e => setFilterGroup(e.target.value)}
            className="bg-[#161a26] border border-[#252a37] text-[#f0f2f7] text-xs rounded-md px-2 py-1.5">
            <option value="all">All Groups</option>
            {groups.map(g => <option key={g} value={g}>Group {g}</option>)}
          </select>
        </div>
      </div>

      {sportFormats.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setFilterSport('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${filterSport==='all'?'bg-[#f5c518] text-[#0b0f18] border-[#f5c518]':'bg-[#161a26] text-[#8a93a3] border-[#252a37] hover:border-[#8a93a3]'}`}>
            All sports
          </button>
          {sportFormats.map(sf => (
            <button key={sf.sport_code} onClick={() => setFilterSport(sf.sport_code)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors flex items-center gap-1 ${filterSport===sf.sport_code?'bg-[#f5c518] text-[#0b0f18] border-[#f5c518]':'bg-[#161a26] text-[#8a93a3] border-[#252a37] hover:border-[#8a93a3]'}`}>
              <span>{SPORT_ICONS_ENC[sf.sport_code]||'🏅'}</span>{sf.sport_name} ({encounters.filter(e => e.sport_results?.some(sr => sr.sport_code === sf.sport_code)).length})
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i=><Skeleton key={i} className="h-20 bg-[#1d2230]"/>)}</div>
      ) : filtered.length === 0 ? (
        <Card className="bg-[#161a26] border-[#252a37]">
          <CardContent className="p-8 text-center">
            <Trophy className="h-12 w-12 text-[#8a93a3] mx-auto mb-3"/>
            <p className="text-[#f0f2f7] font-medium mb-1">No encounters yet</p>
            <p className="text-sm text-[#8a93a3]">Go to Groups tab → assign teams → Generate Matches</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedBySection).map(([sectionName, sectionEncs]) => {
            const isKO = sectionName === 'Group KO';
            // For KO section, sub-group by sport_code
            const koSports = isKO ? (() => {
              const bySport = {};
              const ROUND_ORDER = { sf: 1, final: 2, '3rd': 3 };
              sectionEncs.forEach(e => {
                const sc = e.sport_code || e.sport_results?.[0]?.sport_code || 'all';
                if (!bySport[sc]) bySport[sc] = [];
                bySport[sc].push(e);
              });
              // Sort each sport's encounters: sf first, then final
              Object.values(bySport).forEach(arr => arr.sort((a,b) => (ROUND_ORDER[a.round_type]||9)-(ROUND_ORDER[b.round_type]||9)));
              return bySport;
            })() : null;

            return (
            <div key={sectionName}>
              <div className="flex items-center gap-2 mb-3">
                <div className="h-0.5 w-4 rounded-full" style={{ backgroundColor: isKO ? '#f5c518' : (GROUP_COLORS[sectionName.split(' ')[1]] || '#252a37') }}/>
                <p className={`text-xs font-bold uppercase tracking-widest ${isKO ? 'text-[#f5c518]' : 'text-[#8a93a3]'}`}>{isKO ? '🏆 Knockout Stage' : sectionName}</p>
                {/* Show seed labels for each group */}
                {!isKO && sectionName.startsWith('GROUP ') && (() => {
                  const seeds = [...new Set(sectionEncs.flatMap(e => [e.home_seed, e.away_seed]).filter(Boolean))].sort();
                  return seeds.length > 0 ? (
                    <div className="flex items-center gap-1">
                      {seeds.map(s => <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-[#1d2230] text-[#8a93a3] font-mono">{s}</span>)}
                    </div>
                  ) : null;
                })()}
                <div className="flex-1 h-0.5 rounded-full bg-[#252a37]"/>
                {!isKO && sectionEncs[0]?.scheduled_at && (
                  <span className="text-[10px] text-[#f5c518] font-medium">
                    {new Date(sectionEncs[0].scheduled_at).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}
                  </span>
                )}
              </div>

              {/* KO: sport sub-sections */}
              {isKO ? (
                <div className="space-y-5">
                  {Object.entries(koSports).map(([sc, sportEncs]) => (
                    <div key={sc}>
                      {/* Sport sub-header */}
                      <div className="flex items-center gap-2 mb-2 pl-1">
                        <span className="text-base"><SportIcon sport={sc} size={20}/></span>
                        <span className="text-xs font-bold text-[#f0f2f7] capitalize">{sc.replace(/_/g,' ')}</span>
                        <span className="text-[10px] text-[#8a93a3] bg-[#1d2230] px-2 py-0.5 rounded-full">
                          {sportEncs.filter(e=>e.round_type==='sf').length} Semis · 1 Final
                        </span>
                        <div className="flex-1 h-px bg-[#252a37]"/>
                      </div>
                      <div className="space-y-2 pl-2 border-l-2 border-[rgba(245,197,24,0.2)]">
                        {sportEncs.map(enc => {
                          const isDone = enc.status === 'completed';
                          const isLive = enc.status === 'in_progress';
                          const isExpanded = expandedEnc === enc.id;
                          const isFinal = enc.round_type === 'final';
                          return renderEncounterCard(enc, isDone, isLive, isExpanded, isFinal);
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
              <div className="space-y-2">
                {sectionEncs.map(enc => {
                  const isDone = enc.status === 'completed';
                  const isLive = enc.status === 'in_progress';
                  const isExpanded = expandedEnc === enc.id;
                  return renderEncounterCard(enc, isDone, isLive, isExpanded, false);
                })}
              </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      {/* Edit Teams dialog — KO encounters */}
      <Dialog open={!!editTeamsTarget} onOpenChange={()=>setEditTeamsTarget(null)}>
        <DialogContent className="bg-[#161a26] border-[#252a37] max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7] flex items-center gap-2">
              <Users className="h-4 w-4 text-[#f5c518]"/>
              Override Teams — {editTeamsTarget?.match_label || 'Knockout Match'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-xs text-[#8a93a3]">Manually assign teams for this knockout match, overriding auto-seeding.</p>
            <div>
              <Label className="text-[#8a93a3] text-xs mb-1 block">Home Team</Label>
              <select
                value={editTeamsForm.home_team_id}
                onChange={e => setEditTeamsForm(f => ({...f, home_team_id: e.target.value}))}
                className="w-full bg-[#1d2230] border border-[#252a37] text-[#f0f2f7] text-sm rounded-md px-3 py-2"
                data-testid="ko-home-team-select">
                <option value="">— TBD —</option>
                {orgTeams.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-[#8a93a3] text-xs mb-1 block">Away Team</Label>
              <select
                value={editTeamsForm.away_team_id}
                onChange={e => setEditTeamsForm(f => ({...f, away_team_id: e.target.value}))}
                className="w-full bg-[#1d2230] border border-[#252a37] text-[#f0f2f7] text-sm rounded-md px-3 py-2"
                data-testid="ko-away-team-select">
                <option value="">— TBD —</option>
                {orgTeams.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setEditTeamsTarget(null)} className="border-[#252a37] text-[#f0f2f7]">Cancel</Button>
            <Button onClick={saveEditTeams} disabled={savingTeams} className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]" data-testid="ko-teams-save-button">
              {savingTeams ? 'Saving…' : 'Save Teams'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!scheduleTarget} onOpenChange={()=>setScheduleTarget(null)}>
        <DialogContent className="bg-[#161a26] border-[#252a37] max-w-sm">
          <DialogHeader><DialogTitle className="text-[#f0f2f7]">Schedule Match</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-[#8a93a3]">Date & Time</Label><Input type="datetime-local" value={schedForm.scheduled_at} onChange={e=>setSchedForm({...schedForm,scheduled_at:e.target.value})} className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1"/></div>
            <div><Label className="text-[#8a93a3]">Venue / Court</Label><Input value={schedForm.venue} onChange={e=>setSchedForm({...schedForm,venue:e.target.value})} placeholder="e.g., Court 1, Table 3" className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1"/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setScheduleTarget(null)} className="border-[#252a37] text-[#f0f2f7]">Cancel</Button>
            <Button onClick={saveSchedule} className="bg-[#f5c518] text-[#0b0f18]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {scoreTarget && <EncounterScoreDialog enc={scoreTarget} tournamentId={tournamentId} orgSlug={orgSlug} onClose={()=>{setScoreTarget(null);fetchEncounters();}}/>}

      {/* ── Lineup Builder Dialog ── */}
      {lineupTarget && (
        <Dialog open={!!lineupTarget} onOpenChange={v => !v && setLineupTarget(null)}>
          <DialogContent className="bg-[#161a26] border-[#252a37] max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-[#f0f2f7] flex items-center gap-2">
                <Users className="h-4 w-4 text-[#22c55e]"/>
                Set Lineup — {lineupTarget.home_team?.name} vs {lineupTarget.away_team?.name}
              </DialogTitle>
            </DialogHeader>

            {/* Column headers */}
            <div className="grid grid-cols-[80px_1fr_1fr_1fr_1fr] gap-2 text-[10px] font-bold text-[#8a93a3] uppercase tracking-widest px-1">
              <span>Match</span>
              <span className="text-center text-[#3b82f6]">Home P1</span>
              <span className="text-center text-[#3b82f6]">Home P2</span>
              <span className="text-center text-[#ef4444]">Away P1</span>
              <span className="text-center text-[#ef4444]">Away P2</span>
            </div>

            {/* Player usage counters */}
            {lineupTarget && (() => {
              const maxPP = lineupTarget._maxPerPlayer || 99;
              const minPP = lineupTarget._minPerPlayer || 1;
              const countUsage = (side) => {
                const counts = {};
                lineupSlots.forEach(s => {
                  const p1 = side==='home' ? s.home1 : s.away1;
                  const p2 = side==='home' ? s.home2 : s.away2;
                  if (p1) counts[p1] = (counts[p1]||0)+1;
                  if (p2) counts[p2] = (counts[p2]||0)+1;
                });
                return counts;
              };
              const homeCount = countUsage('home');
              const awayCount = countUsage('away');
              const overHome = Object.entries(homeCount).filter(([,c])=>c>maxPP);
              const overAway = Object.entries(awayCount).filter(([,c])=>c>maxPP);
              if (overHome.length > 0 || overAway.length > 0) return (
                <div className="bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.3)] rounded-xl px-3 py-2 text-xs text-[#ef4444]">
                  ⚠ Limit exceeded (max {maxPP}/player):
                  {[...overHome.map(([n,c])=>`${n} (${c}H)`), ...overAway.map(([n,c])=>`${n} (${c}A)`)].join(', ')}
                </div>
              );
              return null;
            })()}

            <div className="space-y-2">
              {lineupSlots.map((slot, i) => {
                const COMBO_COLORS = { CC:'#22c55e', AA:'#f5c518', AC:'#3b82f6' };
                const col = COMBO_COLORS[slot.combo] || '#8a93a3';
                // Filter players by combo type
                const filterBy = (roster, pos) => {
                  if (!slot.combo) return roster;
                  const cat = (c) => (c||'').toLowerCase().trim();
                  const needsAce   = (slot.combo==='AA') || (slot.combo==='AC' && pos==='home') || (slot.combo==='CA' && pos==='away');
                  const needsChamp = (slot.combo==='CC') || (slot.combo==='AC' && pos==='away') || (slot.combo==='CA' && pos==='home');
                  if (needsAce)   return roster.filter(p => cat(p.category)==='ace'      || p.is_platinum);
                  if (needsChamp) return roster.filter(p => cat(p.category)==='champion' || (cat(p.category)!=='ace' && !p.is_platinum));
                  return roster;
                };
                const homePool = filterBy(lineupRosters.home, 'home');
                const awayPool = filterBy(lineupRosters.away, 'away');
                const getRequiredCat = (combo, pos) => {
                  if (!combo) return null;
                  if (combo === 'AA') return 'Ace';
                  if (combo === 'CC') return 'Champion';
                  if (combo === 'AC') return pos === 'home' ? 'Ace' : 'Champion';
                  if (combo === 'CA') return pos === 'home' ? 'Champion' : 'Ace';
                  return null;
                };
                const makeSelect = (val, onChange, pool, placeholder, usageCounts, maxPP, pos, isSub) => {
                  const reqCat = getRequiredCat(slot.combo, pos);
                  return (
                  <div>
                    {isSub && (
                      <p className="text-[9px] text-[#f59e0b] mb-0.5 px-1">⚠ No {reqCat} players left — picking sub</p>
                    )}
                    <select value={val} onChange={e => onChange(e.target.value)}
                      className={`w-full bg-[#0b0f18] border text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-[#f5c518] ${
                        pool.length === 0 ? 'border-[rgba(239,68,68,0.4)] text-[#ef4444]'
                        : isSub ? 'border-[rgba(245,158,11,0.5)] text-[#f0f2f7]'
                        : 'border-[#252a37] text-[#f0f2f7]'
                      }`}>
                      <option value="">{pool.length === 0 ? `⚠ No ${reqCat||'matching'} players` : placeholder}</option>
                      {pool.map(p => {
                        const name = p.player_name||p.full_name;
                        const count = usageCounts[name] || 0;
                        const atMax = count >= (maxPP||99) && name !== val;
                        return (
                          <option key={p.player_id||p.id} value={name}>
                            {name}{count > 0 ? ` (${count}/${maxPP})` : ''}{atMax ? ' ⚠ over limit' : ''}
                          </option>
                        );
                      })}
                    </select>
                    {pool.length === 0 && reqCat && (
                      <p className="text-[9px] text-[#ef4444] mt-0.5 px-1">
                        Add <strong>{reqCat}</strong> category players to this team first
                      </p>
                    )}
                  </div>
                  );
                };
                return (
                  <div key={i} className="bg-[#1d2230] rounded-xl border border-[#252a37] p-3">
                    {/* Slot header */}
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[10px] font-bold text-[#8a93a3]">{slot.slot}</span>
                      <span className="text-[10px] font-black px-2 py-0.5 rounded-full"
                        style={{ color: col, backgroundColor:`${col}18`, border:`1px solid ${col}40` }}>
                        {slot.combo} · {COMBO_LABELS[slot.combo] || slot.combo}
                      </span>
                    </div>
                    {/* Player selects */}
                    <div className="grid grid-cols-2 gap-3">
                      {(() => {
                        const maxPP = lineupTarget._maxPerPlayer || 99;
                        const hCount = {}; const aCount = {};
                        lineupSlots.forEach(s => {
                          if (s.home1) hCount[s.home1]=(hCount[s.home1]||0)+1;
                          if (s.home2) hCount[s.home2]=(hCount[s.home2]||0)+1;
                          if (s.away1) aCount[s.away1]=(aCount[s.away1]||0)+1;
                          if (s.away2) aCount[s.away2]=(aCount[s.away2]||0)+1;
                        });
                        return (
                          <>
                            <div className="space-y-1.5">
                              <p className="text-[9px] text-[#3b82f6] font-bold uppercase tracking-wider">{lineupTarget.home_team?.name}</p>
                              {(() => {
                                // Sub mode if: no category players, OR all category players are over limit
                                const allMaxed = homePool.length > 0 && homePool.every(p => (hCount[p.player_name||p.full_name]||0) >= maxPP);
                                const isSubP1 = homePool.length === 0 || allMaxed;
                                const pool1 = isSubP1 ? lineupRosters.home : homePool;
                                return makeSelect(slot.home1, v => setLineupSlots(prev => prev.map((s,j) => j===i?{...s,home1:v}:s)), pool1, 'P1…', hCount, maxPP, 'home', isSubP1);
                              })()}
                              {(() => {
                                const hp2 = homePool.filter(p => (p.player_name||p.full_name)!==slot.home1);
                                const allMaxed = hp2.length > 0 && hp2.every(p => (hCount[p.player_name||p.full_name]||0) >= maxPP);
                                const isSub = hp2.length === 0 || allMaxed;
                                const pool2 = isSub ? lineupRosters.home.filter(p => (p.player_name||p.full_name)!==slot.home1) : hp2;
                                return makeSelect(slot.home2, v => setLineupSlots(prev => prev.map((s,j) => j===i?{...s,home2:v}:s)), pool2, 'P2…', hCount, maxPP, 'home', isSub);
                              })()}
                            </div>
                            <div className="space-y-1.5">
                              <p className="text-[9px] text-[#ef4444] font-bold uppercase tracking-wider">{lineupTarget.away_team?.name}</p>
                              {(() => {
                                const allMaxed = awayPool.length > 0 && awayPool.every(p => (aCount[p.player_name||p.full_name]||0) >= maxPP);
                                const isSubP1 = awayPool.length === 0 || allMaxed;
                                const pool1 = isSubP1 ? lineupRosters.away : awayPool;
                                return makeSelect(slot.away1, v => setLineupSlots(prev => prev.map((s,j) => j===i?{...s,away1:v}:s)), pool1, 'P1…', aCount, maxPP, 'away', isSubP1);
                              })()}
                              {(() => {
                                const ap2 = awayPool.filter(p => (p.player_name||p.full_name)!==slot.away1);
                                const allMaxed = ap2.length > 0 && ap2.every(p => (aCount[p.player_name||p.full_name]||0) >= maxPP);
                                const isSub = ap2.length === 0 || allMaxed;
                                const pool2 = isSub ? lineupRosters.away.filter(p => (p.player_name||p.full_name)!==slot.away1) : ap2;
                                return makeSelect(slot.away2, v => setLineupSlots(prev => prev.map((s,j) => j===i?{...s,away2:v}:s)), pool2, 'P2…', aCount, maxPP, 'away', isSub);
                              })()}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                );
              })}
            </div>

            {lineupRosters.home.length === 0 && (
              <p className="text-xs text-[#8a93a3] text-center py-2">
                ⚠ No roster found. Add players to both teams first in the Teams & Players tab.
              </p>
            )}

            <DialogFooter className="mt-2">
              <Button variant="outline" onClick={() => setLineupTarget(null)} className="border-[#252a37] text-[#f0f2f7]">Cancel</Button>
              <Button onClick={saveLineup} disabled={savingLineup}
                className="bg-[#22c55e] text-white hover:bg-[#16a34a]">
                {savingLineup ? 'Saving…' : 'Save Lineup'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {detailTarget && <MatchDetailDialog enc={detailTarget} orgSlug={orgSlug} onClose={()=>setDetailTarget(null)}/>}
    </div>
  );
}
// ──────────────────────────────────────────────────────────────
// Encounter Score Dialog
// ──────────────────────────────────────────────────────────────
function EncounterScoreDialog({ enc, tournamentId, orgSlug, onClose }) {
  const [data, setData] = useState(JSON.parse(JSON.stringify(enc)));
  const [saving, setSaving] = useState(false);
  const [activeSport, setActiveSport] = useState(data.sport_results?.[0]?.sport_code || null);
  const [teamRosters, setTeamRosters] = useState({ home: [], away: [] });
  const [substituteMatches, setSubstituteMatches] = useState({}); // mIdx → true if substitute ON

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchRosters(); }, [enc.id]);

  const fetchRosters = async () => {
    try {
      const token = localStorage.getItem('btccp_access_token');
      const res = await fetch(`/api/orgs/${orgSlug}/encounters/${enc.id}/team-rosters`,
        { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setTeamRosters(d); }
    } catch (err) {
      console.warn('Failed to load team rosters for encounter:', err?.message);
    }
  };
  const getPlayers = (side, tier, sport_code) => {
    const roster = teamRosters[side] || [];
    // Filter by sport first — only show players who play this sport
    const bySport = sport_code
      ? roster.filter(r => !r.sports?.length || r.sports.includes(sport_code))
      : roster;
    // Then filter by platinum tier if needed
    if (tier === 'platinum') return bySport.filter(r => r.is_platinum === true || r.platinum_sports?.includes(sport_code));
    return bySport;
  };

  // Game-by-game scoring helpers
  const getGamesPerMatch = (sport_code) => {
    const def = DEFAULT_COUNTS[sport_code];
    return def?.games_per_match || 1;
  };

  const updateGame = (sport_code, matchIdx, gameIdx, side, score) => {
    setData(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const sr = next.sport_results.find(s => s.sport_code === sport_code);
      if (!sr) return next;
      const m = sr.matches[matchIdx];
      if (!m.games) m.games = [];
      while (m.games.length <= gameIdx) m.games.push({ home: 0, away: 0 });
      m.games[gameIdx][side] = parseInt(score) || 0;
      // Recalculate rubber winner from games
      const minDiff = DEFAULT_COUNTS[sport_code]?.min_diff ?? 2;
      const gp = DEFAULT_COUNTS[sport_code]?.game_points ?? 11;
      const total = getGamesPerMatch(sport_code);
      const toWin = Math.ceil(total / 2);
      let hw = 0, aw = 0;
      m.games.forEach(g => {
        const hs = g.home || 0, as_ = g.away || 0;
        if (hs > 0 || as_ > 0) {
          const winPts = Math.max(gp, Math.max(hs, as_));
          if (hs >= winPts && hs - as_ >= minDiff) hw++;
          else if (as_ >= winPts && as_ - hs >= minDiff) aw++;
        }
      });
      m.home_score = hw;
      m.away_score = aw;
      if (hw >= toWin) { m.winner = 'home'; m.status = 'completed'; }
      else if (aw >= toWin) { m.winner = 'away'; m.status = 'completed'; }
      else { m.winner = null; m.status = 'scheduled'; }
      return next;
    });
  };

  const updateMatch = (sport_code, matchIdx, field, value) => {
    setData(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const sr = next.sport_results.find(s => s.sport_code === sport_code);
      if (sr) {
        sr.matches[matchIdx][field] = value;
        // Auto-set winner based on scores if both scores entered
        const m = sr.matches[matchIdx];
        if (field === 'home_score' || field === 'away_score') {
          const hs = parseInt(m.home_score) || 0;
          const as = parseInt(m.away_score) || 0;
          if (hs > 0 || as > 0) {
            m.winner = hs > as ? 'home' : as > hs ? 'away' : 'draw';
            m.status = 'completed';
          }
        }
      }
      return next;
    });
  };

  const updateSuperMatch = (sport_code, field, value) => {
    setData(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const sr = next.sport_results.find(s => s.sport_code === sport_code);
      if (sr) {
        if (!sr.super_match) sr.super_match = {};
        sr.super_match[field] = value;
        if (field === 'winner') sr.super_match.status = 'completed';
      }
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    const token = localStorage.getItem('btccp_access_token');
    const res = await fetch(`/api/orgs/${orgSlug}/tournaments/${tournamentId}/encounters/${enc.id}`, {
      method: 'PATCH',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ sport_results: data.sport_results }),
    });
    if (res.ok) {
      toast.success('Scores saved!');
      onClose();
    } else {
      const d = await res.json();
      toast.error(d.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const activeSr = data.sport_results?.find(s => s.sport_code === activeSport);
  const homeTeam = enc.home_team || {};
  const awayTeam = enc.away_team || {};

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-[#161a26] border-[#252a37] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="encounter-score-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#f0f2f7] text-base">
            Score Entry: <span className="text-[#f5c518]">{homeTeam.name}</span> vs <span className="text-[#f5c518]">{awayTeam.name}</span>
          </DialogTitle>
        </DialogHeader>

        {/* Sport tabs */}
        <div className="flex gap-2 flex-wrap mb-4">
          {data.sport_results?.map(sr => {
            const wins_h = sr.matches.filter(m=>m.winner==='home').length;
            const wins_a = sr.matches.filter(m=>m.winner==='away').length;
            const hasScore = wins_h > 0 || wins_a > 0;
            return (
              <button key={sr.sport_code} onClick={() => setActiveSport(sr.sport_code)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  activeSport===sr.sport_code ? 'bg-[#f5c518] text-[#0b0f18]' : 'bg-[#1d2230] text-[#8a93a3] hover:text-[#f0f2f7]'
                }`}>
                {sr.sport_name}
                {hasScore && ` (${wins_h}-${wins_a})`}
                {sr.sport_winner==='tie_pending' && ' ⚡'}
              </button>
            );
          })}
        </div>

        {activeSr && (
          <div className="space-y-3">
            {/* Split matches into Platinum and Regular tiers */}
            {(() => {
              const platMatches = activeSr.matches.filter(m => m.tier === 'platinum');
              const regMatches  = activeSr.matches.filter(m => m.tier !== 'platinum');
              const hasTiers    = platMatches.length > 0;

              const MatchRow = ({ m, idx }) => {
                const mIdx = activeSr.matches.indexOf(m);
                const isDoubles   = m.type === 'doubles';
                const gamesPerM   = getGamesPerMatch(activeSr.sport_code);
                const needsGames  = gamesPerM > 1;  // TT: best of 3
                const isPlat      = m.tier === 'platinum';
                const isSub       = !!substituteMatches[mIdx];
                const homePlayers = getPlayers('home', (isPlat && !isSub) ? 'platinum' : null, activeSr.sport_code);
                const awayPlayers = getPlayers('away', (isPlat && !isSub) ? 'platinum' : null, activeSr.sport_code);
                const hasRosterData = (getPlayers('home', null, activeSr.sport_code).length > 0) ||
                                      (getPlayers('away', null, activeSr.sport_code).length > 0);

                // For game-by-game scoring: get or init games array
                const games = m.games || [];
                const maxGames = gamesPerM;
                const toWin = Math.ceil(maxGames / 2);
                const hw = (m.home_score || 0);
                const aw = (m.away_score || 0);
                // Show game 3 only if score is tied (1-1 in best of 3)
                const showGame3 = maxGames >= 3 && (hw >= 1 && aw >= 1);

                return (
                  <div className={`border-b border-[#252a37]/50 last:border-0 ${
                    m.winner === 'home' ? 'bg-[rgba(34,197,94,0.04)]' :
                    m.winner === 'away' ? 'bg-[rgba(239,68,68,0.04)]' : ''
                  }`}>
                    {/* Player assignment row */}
                    {hasRosterData && (
                      <div className="px-3 pt-2 pb-1 space-y-1 text-xs">
                        {/* Substitute toggle for platinum matches */}
                        {isPlat && (
                          <div className="flex justify-end mb-1">
                            <button
                              onClick={() => setSubstituteMatches(prev => ({ ...prev, [mIdx]: !isSub }))}
                              className={`text-[10px] font-bold px-2.5 py-1 rounded-full border transition-colors ${
                                isSub
                                  ? 'bg-[rgba(239,68,68,0.12)] border-[rgba(239,68,68,0.4)] text-[#ef4444]'
                                  : 'bg-[#1d2230] border-[#252a37] text-[#8a93a3] hover:border-[rgba(239,68,68,0.4)] hover:text-[#ef4444]'
                              }`}>
                              {isSub ? '⚠ Substitute ON' : 'Platinum absent? →'}
                            </button>
                          </div>
                        )}
                        <div className="grid grid-cols-[2.5rem_1fr_1rem_1fr] gap-1">
                          <span/>
                          <select value={m.home_player || ''}
                            onChange={e => updateMatch(activeSr.sport_code, mIdx, 'home_player', e.target.value)}
                            className="bg-[#1d2230] border border-[#252a37] text-[#f0f2f7] rounded px-1.5 py-1 text-xs w-full truncate">
                            <option value="">{isDoubles ? 'Home P1…' : 'Home player…'}</option>
                            {homePlayers.map(r => <option key={r.player_id} value={r.player_name}>{r.player_name}</option>)}
                          </select>
                          <span className="text-center text-[#8a93a3]">v</span>
                          <select value={m.away_player || ''}
                            onChange={e => updateMatch(activeSr.sport_code, mIdx, 'away_player', e.target.value)}
                            className="bg-[#1d2230] border border-[#252a37] text-[#f0f2f7] rounded px-1.5 py-1 text-xs w-full truncate">
                            <option value="">{isDoubles ? 'Away P1…' : 'Away player…'}</option>
                            {awayPlayers.map(r => <option key={r.player_id} value={r.player_name}>{r.player_name}</option>)}
                          </select>
                        </div>
                        {/* Doubles: second player row */}
                        {isDoubles && (
                          <div className="grid grid-cols-[2.5rem_1fr_1rem_1fr] gap-1">
                            <span/>
                            <select value={m.home_player2 || ''}
                              onChange={e => updateMatch(activeSr.sport_code, mIdx, 'home_player2', e.target.value)}
                              className="bg-[#1d2230] border border-[#252a37] text-[#f0f2f7] rounded px-1.5 py-1 text-xs w-full truncate">
                              <option value="">Home P2…</option>
                              {homePlayers.filter(r => r.player_name !== m.home_player).map(r =>
                                <option key={r.player_id} value={r.player_name}>{r.player_name}</option>)}
                            </select>
                            <span className="text-center text-[#8a93a3]">&</span>
                            <select value={m.away_player2 || ''}
                              onChange={e => updateMatch(activeSr.sport_code, mIdx, 'away_player2', e.target.value)}
                              className="bg-[#1d2230] border border-[#252a37] text-[#f0f2f7] rounded px-1.5 py-1 text-xs w-full truncate">
                              <option value="">Away P2…</option>
                              {awayPlayers.filter(r => r.player_name !== m.away_player).map(r =>
                                <option key={r.player_id} value={r.player_name}>{r.player_name}</option>)}
                            </select>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Score row */}
                    {needsGames ? (
                      /* Best-of-3: show individual games */
                      <div className="px-3 py-2">
                        <div className="flex items-center gap-1 mb-1">
                          <span className="text-xs font-bold w-10 text-center" style={{color:m.tier==='platinum'?'#f5c518':'#8a93a3'}}>{m.label||idx+1}</span>
                          <span className={`text-xs font-bold px-1.5 ${hw>=toWin?'text-[#22c55e]':'text-[#8a93a3]'}`}>
                            {m.winner ? `${hw}–${aw} (${m.winner==='home'?homeTeam.short_name||'H':'away win'})` : `${hw}–${aw}`}
                          </span>
                        </div>
                        {[0,1,2].slice(0, showGame3 ? maxGames : Math.min(2, maxGames)).map(gi => {
                          const g = games[gi] || { home:0, away:0 };
                          const done = hw >= toWin || aw >= toWin;
                          return (
                            <div key={gi} className="grid grid-cols-[2rem_3rem_2rem_3rem_2rem] gap-1 items-center mb-1">
                              <span className="text-[10px] text-[#8a93a3] text-center">G{gi+1}</span>
                              <Input type="number" min="0" value={g.home||''} placeholder="0"
                                disabled={done && gi >= (hw+aw)}
                                onChange={e => updateGame(activeSr.sport_code, mIdx, gi, 'home', e.target.value)}
                                className="h-7 bg-[#161a26] border-[#252a37] text-[#f0f2f7] text-center text-sm px-1"/>
                              <span className="text-center text-[#8a93a3] text-xs">:</span>
                              <Input type="number" min="0" value={g.away||''} placeholder="0"
                                disabled={done && gi >= (hw+aw)}
                                onChange={e => updateGame(activeSr.sport_code, mIdx, gi, 'away', e.target.value)}
                                className="h-7 bg-[#161a26] border-[#252a37] text-[#f0f2f7] text-center text-sm px-1"/>
                              {g.home > 0 || g.away > 0 ? (
                                <span className={`text-xs font-bold text-center ${g.home>g.away?'text-[#22c55e]':'text-[#ef4444]'}`}>
                                  {g.home>g.away?'H':'A'}
                                </span>
                              ) : <span/>}
                            </div>
                          );
                        })}
                        {/* Show game 3 hint */}
                        {maxGames >= 3 && !showGame3 && !m.winner && (
                          <p className="text-[10px] text-[#8a93a3] italic ml-8">Game 3 available if tied 1-1</p>
                        )}
                      </div>
                    ) : (
                      /* Single score row (Chess, Carrom, Badminton, Pickleball) */
                      <div className="grid grid-cols-[2.5rem_1fr_5rem_3rem_5rem_1fr] gap-1 items-center px-3 py-2 text-sm">
                        <div className="flex flex-col items-center">
                          <span className="text-xs font-bold" style={{color:m.tier==='platinum'?'#f5c518':'#8a93a3'}}>{m.label||idx+1}</span>
                          <span className={`text-[10px] px-1 rounded ${m.type==='doubles'?'bg-[rgba(59,130,246,0.2)] text-[#3b82f6]':'bg-[rgba(139,92,246,0.2)] text-[#8b5cf6]'}`}>
                            {m.type[0].toUpperCase()}
                          </span>
                        </div>
                        <span className={`text-xs ${m.winner==='home'?'text-[#22c55e] font-bold':'text-[#8a93a3]'}`}>
                          {m.home_player?<span className="truncate block max-w-[80px] text-[10px]">{m.home_player.split(' ')[0]}</span>:(m.winner==='home'?'✓':'')}
                        </span>
                        <Input type="number" min="0" value={m.home_score||''} placeholder="0"
                          onChange={e => updateMatch(activeSr.sport_code, mIdx, 'home_score', e.target.value)}
                          className="h-7 bg-[#161a26] border-[#252a37] text-[#f0f2f7] text-center text-sm px-1"/>
                        <div className="text-center">
                          <select value={m.winner||''}
                            onChange={e => { updateMatch(activeSr.sport_code, mIdx, 'winner', e.target.value); if(e.target.value) updateMatch(activeSr.sport_code, mIdx, 'status', 'completed'); }}
                            className="w-full bg-[#1d2230] border border-[#252a37] text-[#8a93a3] text-xs rounded py-1 text-center">
                            <option value="">-</option><option value="home">H</option><option value="away">A</option><option value="draw">D</option>
                          </select>
                        </div>
                        <Input type="number" min="0" value={m.away_score||''} placeholder="0"
                          onChange={e => updateMatch(activeSr.sport_code, mIdx, 'away_score', e.target.value)}
                          className="h-7 bg-[#161a26] border-[#252a37] text-[#f0f2f7] text-center text-sm px-1"/>
                        <span className={`text-xs text-right ${m.winner==='away'?'text-[#22c55e] font-bold':'text-[#8a93a3]'}`}>
                          {m.away_player?<span className="truncate block max-w-[80px] text-[10px] text-right">{m.away_player.split(' ')[0]}</span>:(m.winner==='away'?'✓':'')}
                        </span>
                      </div>
                    )}
                  </div>
                );
              };

              const TableHeader = () => (
                <div className="grid grid-cols-[2.5rem_1fr_5rem_3rem_5rem_1fr] gap-1 px-3 py-2 border-b border-[#252a37] text-xs text-[#8a93a3]">
                  <span>#</span>
                  <span className="truncate">{homeTeam.short_name || homeTeam.name}</span>
                  <span className="text-center">Score</span>
                  <span className="text-center">vs</span>
                  <span className="text-center">Score</span>
                  <span className="truncate text-right">{awayTeam.short_name || awayTeam.name}</span>
                </div>
              );

              return (
                <>
                  {hasTiers && platMatches.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-[#f5c518] text-sm">★</span>
                        <p className="text-xs font-semibold text-[#f5c518] uppercase tracking-wider">Platinum Matches ({platMatches.filter(m=>m.winner==='home').length} – {platMatches.filter(m=>m.winner==='away').length})</p>
                      </div>
                      <div className="bg-[#1d2230] rounded-lg overflow-hidden border border-[rgba(245,197,24,0.2)]">
                        <TableHeader/>
                        {platMatches.map((m, i) => <MatchRow key={m.slot||m.label||i} m={m} idx={i}/>)}
                      </div>
                    </div>
                  )}

                  {regMatches.length > 0 && (
                    <div>
                      {hasTiers && (
                        <div className="flex items-center gap-2 mb-1.5">
                          <p className="text-xs font-semibold text-[#8a93a3] uppercase tracking-wider">Regular Matches ({regMatches.filter(m=>m.winner==='home').length} – {regMatches.filter(m=>m.winner==='away').length})</p>
                        </div>
                      )}
                      <div className="bg-[#1d2230] rounded-lg overflow-hidden">
                        {!hasTiers && <TableHeader/>}
                        {hasTiers && (
                          <div className="grid grid-cols-[2.5rem_1fr_5rem_3rem_5rem_1fr] gap-1 px-3 py-2 border-b border-[#252a37] text-xs text-[#8a93a3]">
                            <span>#</span>
                            <span className="truncate">{homeTeam.short_name || homeTeam.name}</span>
                            <span className="text-center">Score</span><span className="text-center">vs</span>
                            <span className="text-center">Score</span>
                            <span className="truncate text-right">{awayTeam.short_name || awayTeam.name}</span>
                          </div>
                        )}
                        {regMatches.map((m, i) => <MatchRow key={m.slot||m.label||i} m={m} idx={i}/>)}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}

            {/* Running tally */}
            <div className="bg-[#1d2230] rounded-lg p-3">
              <div className="flex items-center justify-center gap-6 py-1">
                <div className="text-center">
                  <p className="text-2xl font-bold text-[#f0f2f7]">{activeSr.matches.filter(m=>m.winner==='home').length}</p>
                  <p className="text-xs text-[#8a93a3] truncate max-w-[80px]">{homeTeam.short_name||homeTeam.name}</p>
                </div>
                <span className="text-[#8a93a3]">:</span>
                <div className="text-center">
                  <p className="text-2xl font-bold text-[#f0f2f7]">{activeSr.matches.filter(m=>m.winner==='away').length}</p>
                  <p className="text-xs text-[#8a93a3] truncate max-w-[80px]">{awayTeam.short_name||awayTeam.name}</p>
                </div>
              </div>
              {activeSr.matches.some(m=>m.tier==='platinum') && (
                <div className="flex justify-center gap-4 mt-2 text-xs">
                  <span className="text-[#f5c518]">★ Platinum: {activeSr.matches.filter(m=>m.tier==='platinum'&&m.winner==='home').length}-{activeSr.matches.filter(m=>m.tier==='platinum'&&m.winner==='away').length}</span>
                  <span className="text-[#8a93a3]">Regular: {activeSr.matches.filter(m=>m.tier!=='platinum'&&m.winner==='home').length}-{activeSr.matches.filter(m=>m.tier!=='platinum'&&m.winner==='away').length}</span>
                </div>
              )}
            </div>

            {/* Super match if tied */}
            {activeSr.super_match_on_tie && (() => {
              const hw = activeSr.matches.filter(m=>m.winner==='home').length;
              const aw = activeSr.matches.filter(m=>m.winner==='away').length;
              const total = activeSr.matches.length;
              const completed = activeSr.matches.filter(m=>m.winner).length;
              if (completed === total && hw === aw) {
                return (
                  <div className="bg-[rgba(245,197,24,0.1)] border border-[rgba(245,197,24,0.3)] rounded-lg p-3">
                    <p className="text-sm text-[#f5c518] font-semibold mb-2">⚡ Tied {hw}-{aw} — Super Match Required</p>
                    <div className="flex items-center gap-3">
                      <span className="text-[#8a93a3] text-sm">Winner:</span>
                      {[{v:'home',l:homeTeam.name},{v:'away',l:awayTeam.name}].map(opt => (
                        <button key={opt.v} onClick={() => updateSuperMatch(activeSr.sport_code,'winner',opt.v)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            activeSr.super_match?.winner===opt.v ? 'bg-[#f5c518] text-[#0b0f18]' : 'bg-[#1d2230] text-[#8a93a3] hover:text-[#f0f2f7]'
                          }`}>
                          {opt.l}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              }
              return null;
            })()}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#252a37] text-[#f0f2f7]">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]">
            {saving ? 'Saving...' : 'Save Scores'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ──────────────────────────────────────────────────────────────
// Group Standings Tab
// ──────────────────────────────────────────────────────────────
export function GroupStandingsTab({ tournamentId, orgSlug }) {
  const [standings, setStandings] = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => { fetchStandings(); }, [tournamentId]);

  const fetchStandings = async () => {
    setLoading(true);
    const token = localStorage.getItem('btccp_access_token');
    const res = await fetch(`/api/orgs/${orgSlug}/tournaments/${tournamentId}/group-standings`,
      { headers: { 'Authorization': `Bearer ${token}` } });
    const data = await res.json();
    setStandings(Array.isArray(data) ? data : []);
    setLoading(false);
  };

  // Find overall champion (highest pts across all groups)
  const allTeams = standings.flatMap(g => g.standings);
  const champion = allTeams.length > 0 ? allTeams.reduce((a,b) => (a.pts||0) >= (b.pts||0) ? a : b, allTeams[0]) : null;

  if (loading) return <div className="space-y-4">{[1,2].map(i=><Skeleton key={i} className="h-40 bg-[#1d2230]"/>)}</div>;

  if (!standings.length) return (
    <Card className="bg-[#161a26] border-[#252a37]">
      <CardContent className="p-8 text-center">
        <Trophy className="h-12 w-12 text-[#8a93a3] mx-auto mb-3"/>
        <p className="text-[#f0f2f7] font-medium">No standings yet</p>
        <p className="text-sm text-[#8a93a3]">Set up groups and play matches to see standings</p>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="group-standings-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-[#f0f2f7]">Group Standings</h3>
        <Button variant="ghost" size="sm" onClick={fetchStandings} className="text-[#8a93a3]"><RefreshCw className="h-4 w-4"/></Button>
      </div>

      {/* Champion Banner — matching mobile app design */}
      {champion && champion.pts > 0 && (
        <div className="relative overflow-hidden rounded-2xl border border-[rgba(245,197,24,0.4)] bg-gradient-to-br from-[rgba(245,197,24,0.12)] to-[rgba(245,197,24,0.03)] p-5">
          <div className="absolute top-3 right-3 text-4xl opacity-20">🏆</div>
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-full flex items-center justify-center font-bold text-[#0b0f18] text-lg flex-shrink-0"
              style={{ backgroundColor: champion.color || '#f5c518' }}>
              {champion.short_name?.[0] || champion.team_name?.[0]}
            </div>
            <div>
              <p className="text-xs text-[#f5c518] font-bold uppercase tracking-widest mb-0.5">CHAMPION</p>
              <p className="text-xl font-bold text-[#f0f2f7]">{champion.team_name}</p>
              <p className="text-sm text-[#8a93a3]">
                {champion.pts} pts · {champion.wins}W {champion.draws}D {champion.losses}L
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Group tables */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {standings.map(grp => (
          <div key={grp.group_id}>
            <div className="flex items-center gap-2 mb-3">
              <div className="h-7 w-7 rounded flex items-center justify-center text-white font-bold text-sm"
                style={{ backgroundColor: GROUP_COLORS[grp.group_letter] || '#252a37' }}>
                {grp.group_letter}
              </div>
              <h4 className="font-semibold text-[#f0f2f7]">{grp.group_name}</h4>
            </div>
            <Card className="bg-[#10131c] border-[#1e2435]">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#1e2435] bg-[#161a26]">
                      <th className="text-left px-3 py-2.5 text-xs text-[#8a93a3] font-medium w-8">#</th>
                      <th className="text-left px-3 py-2.5 text-xs text-[#8a93a3] font-medium">Team</th>
                      <th className="text-center px-2 py-2.5 text-xs text-[#8a93a3] font-medium">P</th>
                      <th className="text-center px-2 py-2.5 text-xs text-[#8a93a3] font-medium">W</th>
                      <th className="text-center px-2 py-2.5 text-xs text-[#8a93a3] font-medium">L</th>
                      <th className="text-center px-2 py-2.5 text-xs text-[#8a93a3] font-medium font-bold">PTS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grp.standings.map((row, idx) => {
                      const isChampion = champion?.team_id === row.team_id;
                      return (
                        <tr key={row.team_id} className={`border-b border-[#1e2435]/50 last:border-0 ${isChampion ? 'bg-[rgba(245,197,24,0.04)]' : idx < 2 ? 'bg-[rgba(59,130,246,0.02)]' : ''}`}>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-1">
                              <span className={`text-xs font-bold ${idx < 2 ? 'text-[#f5c518]' : 'text-[#8a93a3]'}`}>{idx+1}</span>
                              {idx < 2 && <span className="text-[8px] text-[#3b82f6]">→SF</span>}
                            </div>
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-2">
                              <div className="h-6 w-6 rounded-full flex-shrink-0 flex items-center justify-center text-white text-[10px] font-bold"
                                style={{ backgroundColor: row.color || '#1d2230' }}>
                                {row.short_name?.[0] || row.team_name?.[0]}
                              </div>
                              <span className={`truncate max-w-[100px] text-sm ${isChampion ? 'text-[#f5c518] font-bold' : 'text-[#f0f2f7]'}`}>{row.team_name}</span>
                              {isChampion && <span className="text-xs">🏆</span>}
                            </div>
                          </td>
                          <td className="text-center px-2 py-2.5 text-[#8a93a3] text-xs">{row.played}</td>
                          <td className="text-center px-2 py-2.5 text-[#22c55e] text-xs font-medium">{row.wins}</td>
                          <td className="text-center px-2 py-2.5 text-[#ef4444] text-xs">{row.losses}</td>
                          <td className="text-center px-2 py-2.5 font-bold text-sm" style={{ color: idx === 0 ? '#f5c518' : idx === 1 ? '#f0f2f7' : '#8a93a3' }}>{row.pts}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        ))}
      </div>

      {/* Semi Final Draw */}
      {standings.length >= 2 && (
        <Card className="bg-[#10131c] border-[rgba(245,197,24,0.25)]">
          <CardContent className="p-4">
            <p className="text-sm font-bold text-[#f5c518] mb-3 flex items-center gap-2">
              <Trophy className="h-4 w-4"/>Semi Finals
            </p>
            {/* Check if group stage is complete enough to show real teams */}
            {(() => {
              const allPlayed = standings.every(g => g.standings.some(t => t.played > 0));
              if (!allPlayed) {
                return (
                  <div className="space-y-2">
                    {[
                      { label: 'SF1', desc: 'Group A #1 vs Group B #2' },
                      { label: 'SF2', desc: 'Group B #1 vs Group A #2' },
                    ].map(sf => (
                      <div key={sf.label} className="bg-[#161a26] rounded-xl p-3 flex items-center gap-3 border border-[#252a37]">
                        <span className="text-xs font-bold text-[#f5c518] font-mono w-8">{sf.label}</span>
                        <span className="text-[#8a93a3] text-sm flex-1">{sf.desc}</span>
                        <span className="text-xs text-[#8a93a3] bg-[#1d2230] px-2 py-0.5 rounded">TBD after group stage</span>
                      </div>
                    ))}
                  </div>
                );
              }
              // Show actual teams after group stage
              return (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {standings.map((grp, gi) => {
                    const opp = standings[(gi+1)%standings.length];
                    const myTop = grp.standings[0];
                    const oppBottom = opp?.standings[1];
                    return myTop && oppBottom ? (
                      <div key={gi} className="bg-[#161a26] rounded-xl p-3 flex items-center gap-2 border border-[#252a37]">
                        <div className="h-7 w-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                          style={{ backgroundColor: myTop.color || '#3b82f6' }}>
                          {myTop.team_name?.[0]}
                        </div>
                        <span className="text-[#f0f2f7] font-medium text-sm truncate flex-1">{myTop.team_name}</span>
                        <span className="text-[#8a93a3] text-xs px-2">vs</span>
                        <span className="text-[#f0f2f7] font-medium text-sm truncate flex-1 text-right">{oppBottom.team_name}</span>
                        <div className="h-7 w-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                          style={{ backgroundColor: oppBottom.color || '#ef4444' }}>
                          {oppBottom.team_name?.[0]}
                        </div>
                      </div>
                    ) : null;
                  })}
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

